import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import { Grid, Typography, Tabs, Tab, Icon, Divider, Container } from '@material-ui/core';
import {
  DashboardSettings,
  ExternalProviders,
  FarmStaff,
  FarmSettings,
  EmissionSourceSettings,
  SensorDevices
} from '../../components';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import UsersPage from '../../pages/UsersPage';
import NotificationRulesList from './components/NotificationRulesList';
import TimezoneSettings from './components/TimezoneSettings';
import UserRole from '../../constants/user-role';
import useFeatures, { FeatureType } from '../../hooks/features';
import usePermission from '../../hooks/usePermission';
import { Permission } from '../../constants/permissions';

const FarmSettingsPage: React.FC<any> = () => {
  const { option } = useParams<{ option: string }>();
  const classes = useStyles();
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();
  const { hasPermission } = usePermission();

  /**
   * Sensor devices are Whysor's, so the tab follows the same feature flag as the External Providers panel it
   * sits next to - a farm that cannot configure a provider has no devices to bind. The permission is checked
   * as well as the page's own admin/manager guard, because it is what the backend enforces on all four
   * sensor-device endpoints.
   */
  const canViewSensorDevices =
    hasFeature(FeatureType.USE_EXTERNAL_PROVIDER) && hasPermission(Permission.VIEW_SENSOR_DEVICES);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/farm-settings/dashboard');
        break;
      case 1:
        navigate('/farm-settings/notifications');
        break;
      case 2:
        navigate('/farm-settings/employees');
        break;
      case 3:
        navigate('/farm-settings/settings');
        break;
      case 4:
        navigate('/farm-settings/notification-rules');
        break;
      case 5:
        navigate('/farm-settings/emission-source');
        break;
      case 6:
        navigate('/farm-settings/sensor-devices');
        break;
      default:
        break;
    }
  };

  const getTabIndex = (): number => {
    if (option && option === 'dashboard') {
      return 0;
    }
    if (option && option === 'notifications') {
      return 1;
    }
    if (option && option === 'employees') {
      return 2;
    }
    if (option && option === 'settings') {
      return 3;
    }
    if (option && option === 'emission-source') {
      return 5;
    }
    // Falls back to the first tab when the tab is not available: a `value` MUI has no `<Tab>` for renders an
    // empty tab strip and an orphaned panel.
    if (option && option === 'sensor-devices' && canViewSensorDevices) {
      return 6;
    }
    return 0;
  };

  React.useEffect(() => {
    const tabIndex = getTabIndex();
    setTab(tabIndex);
    // Features arrive asynchronously, so a direct link to a flagged tab resolves to 0 on the first pass and
    // has to be re-resolved once the flag is known.
  }, [option, canViewSensorDevices]);

  const [tab, setTab] = React.useState(getTabIndex());

  return state.role === UserRole.manager || state.role === UserRole.admin ? (
    <Container maxWidth="lg">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item container xs={12} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'engineering'}
                </Icon>
              </Grid>
              <Grid item>
                <Typography component="h2" variant="h6" color="primary">
                  {t('f-settings-farm')}
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                <Tab label={t('f-dashboard')} id="dashTab" value={0} />
                <Tab label={t('f-notifications')} id="settTab" value={1} />
                <Tab label={t('f-employees')} id="usrTab" value={2} />
                <Tab label={t('f-settings')} id="settTab" value={3} />
                <Tab label={t('f-emission-source')} id="emissionTab" value={5} />
                {canViewSensorDevices && <Tab label={t('f-sensor-dev-title')} id="sensorDevicesTab" value={6} />}
              </Tabs>
              <Divider />
            </Grid>
          </Grid>

          <Grid container spacing={2} className={classes.mt3}>
            {tab === 0 && (
              <Grid item md={12}>
                <DashboardSettings />
              </Grid>
            )}
            {tab === 1 && (
              <Grid item md={12}>
                <NotificationRulesList />
              </Grid>
            )}
            {tab === 2 && (
              <>
                <Grid item md={12}>
                  <UsersPage />
                </Grid>

                <Grid item md={12}>
                  <Divider />
                </Grid>

                <Grid item md={12}>
                  <FarmStaff />
                </Grid>
              </>
            )}
            {tab === 3 && (
              <Grid item md={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FarmSettings />
                  </Grid>
                  <Grid item xs={12}>
                    <TimezoneSettings />
                  </Grid>
                  {hasFeature(FeatureType.USE_EXTERNAL_PROVIDER) && (
                    <Grid item xs={12}>
                      <ExternalProviders />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
            {tab === 5 && (
              <Grid item md={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <EmissionSourceSettings />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {tab === 6 && canViewSensorDevices && (
              <Grid item md={12}>
                <SensorDevices />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  ) : (
    <Navigate to={{ pathname: '/dashboard' }} />
  );
};

export default FarmSettingsPage;
